import Decimal from 'decimal.js-light';

import { filterReverseAllocationLineItems } from './filterAllocationLineItems';

import { IBill, ILineItem, IMeter } from '../types/interfaces';

export type AllocatedLineItemMapping = {
  masterLineItem: Partial<ILineItem>;
  allocatedLineItems: Partial<ILineItem>[];
  costDifference: number;
  usageDifference: number;
};

export function getAllocatedLineItemMappings(
  masterBill: Partial<IBill>,
  allocatedBills: Partial<IBill>[],
  meters: Partial<IMeter>[],
): AllocatedLineItemMapping[] {
  if (
    !masterBill ||
    !allocatedBills ||
    !allocatedBills.length ||
    !meters.length
  ) {
    return [];
  }

  const masterLineItems = filterReverseAllocationLineItems(
    masterBill.lineItems,
  );

  const lineItemMappings: AllocatedLineItemMapping[] = masterLineItems?.map(
    (masterLineItem) => {
      const masterMeter = meters.find((m) => m.id === masterLineItem.meterId);

      const allocatedLineItems: Partial<ILineItem>[] = [];

      allocatedBills.forEach((ab) => {
        ab.lineItems?.forEach((li) => {
          // get master meter for the allocated line item
          const subMeter = meters.find((m) => m.id === li.meterId);

          // this is a matching line item if the sub meter's parent matches the master line item's meter and
          // the master line item IDs match.
          // we need to check that the meters still match to ensure that we can save valid line items in the allocation
          if (
            subMeter &&
            subMeter.masterMeterId === masterLineItem.meterId &&
            li.masterLineItemId === masterLineItem.id
          ) {
            allocatedLineItems.push({
              ...li,
              meter: subMeter,
            });
          }
        });
      });

      const usageDifference = new Decimal(masterLineItem.usage || 0)
        .sub(
          allocatedLineItems.reduce(
            (sum, li) => sum.add(li.usage || 0),
            new Decimal(0),
          ),
        )
        .toNumber();
      const costDifference = new Decimal(masterLineItem.cost || 0)
        .sub(
          allocatedLineItems.reduce(
            (sum, li) => sum.add(li.cost || 0),
            new Decimal(0),
          ),
        )
        .toNumber();

      return {
        masterLineItem: {
          ...masterLineItem,
          meter: masterMeter,
        },
        allocatedLineItems,
        costDifference,
        usageDifference,
      };
    },
  );

  // Make sure we have the same number of mapped line items as are in the allocated bills, we cannot edit the allocations if we cannot map all line items
  // Only throw an error if we are not still fetching data
  const mappedAllocatedLineItemCount = lineItemMappings.reduce(
    (count, mapping) => count + mapping.allocatedLineItems.length,
    0,
  );
  const allocatedLineItemCount = allocatedBills.reduce(
    (count, ab) => count + ab.lineItems.length,
    0,
  );

  if (mappedAllocatedLineItemCount !== allocatedLineItemCount) {
    throw new Error(
      `Unable to edit bill allocations for bill ID: ${masterBill.id}. Mapped ${mappedAllocatedLineItemCount} line items, but the allocations contain ${allocatedLineItemCount} line items`,
    );
  }

  return lineItemMappings;
}
